import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout, showToast } from '@components/Layout';
import { useAuth } from '@context/AuthContext';
import { Button, FormInput } from '@components/UI';
import { buildAgreementRedirectPath } from '@/utils/agreements';

// Get redirect URL from query params
const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

function getTwoFactorSetupRedirect(redirectUrl: string) {
    const sanitizedRedirectUrl = redirectUrl.startsWith('/user/enable2fa') ? '/user/portfolios' : redirectUrl;
    return `/user/enable2fa?redirect=${encodeURIComponent(sanitizedRedirectUrl)}`;
}

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { login, isAuthenticated, requiresTwoFactorSetup, hasPendingRequiredAgreements, pendingRequiredAgreements } = useAuth();
    const query = useQuery();
    const navigate = useNavigate();
    const redirectUrl = query.get('redirect') || '/';

    // If already authenticated, redirect to home or the redirect URL
    useEffect(() => {
        if (isAuthenticated) {
            const targetRoute = hasPendingRequiredAgreements
                ? buildAgreementRedirectPath({
                      pendingRequiredAgreements,
                      redirectTarget: redirectUrl
                  })
                : requiresTwoFactorSetup
                  ? getTwoFactorSetupRedirect(redirectUrl)
                  : redirectUrl;

            void navigate(targetRoute, { replace: true });
        }
    }, [hasPendingRequiredAgreements, isAuthenticated, navigate, pendingRequiredAgreements, redirectUrl, requiresTwoFactorSetup]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !password) {
            showToast('Please enter email and password', 'error');
            return;
        }

        // If 2FA is required but not provided
        if (needsTwoFactor && !twoFactorCode) {
            showToast('Please enter your two-factor authentication code', 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            // Pass the 2FA code if it's needed
            await login(email, password, needsTwoFactor ? twoFactorCode : undefined);
            // Redirect will happen automatically due to the useEffect above
            showToast('Login successful', 'success');
        } catch (error: unknown) {
            console.error('Login error:', error);

            const apiError = error as Error;

            // Check if this is a 2FA requirement error
            if (apiError.message === '2FA_REQUIRED') {
                setNeedsTwoFactor(true);
                showToast('Please enter your two-factor authentication code', 'info');
            } else {
                showToast(apiError.message || 'Login failed. Please check your credentials.', 'error');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Layout title="Project 314 - Login">
            <div className="container mx-auto mt-3 text-center">
                <h3 className="text-white mb-3">Login</h3>
                <div className="max-w-md mx-auto mt-2 p-4 bg-zinc-800 rounded-lg shadow-lg">
                    <form id="loginForm" onSubmit={handleSubmit}>
                        <FormInput
                            required
                            className="bg-[#111213] text-[#7c8083] border-[#7c8083] disabled:bg-[#1a1a1a] disabled:text-[#5a5a5a] disabled:border-[#333] disabled:opacity-80"
                            disabled={needsTwoFactor}
                            id="email"
                            label="Email"
                            maxLength={100}
                            minLength={7}
                            name="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <FormInput
                            required
                            className="bg-[#111213] text-[#7c8083] border-[#7c8083] disabled:bg-[#1a1a1a] disabled:text-[#5a5a5a] disabled:border-[#333] disabled:opacity-80"
                            disabled={needsTwoFactor}
                            id="password"
                            label="Password"
                            maxLength={100}
                            minLength={6}
                            name="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        {needsTwoFactor && (
                            <FormInput
                                autoFocus
                                required
                                className="bg-[#111213] text-[#7c8083] border-[#7c8083]"
                                id="twoFactorCode"
                                label="Two-Factor Authentication Code"
                                name="twoFactorCode"
                                placeholder="Enter your 6-digit code"
                                type="text"
                                value={twoFactorCode}
                                onChange={(e) => setTwoFactorCode(e.target.value)}
                            />
                        )}
                        <div className="mt-3">
                            <Button disabled={isSubmitting} type="submit">
                                {isSubmitting ? (
                                    <>
                                        <svg
                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path
                                                className="opacity-75"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                        Loading...
                                    </>
                                ) : needsTwoFactor ? (
                                    'Verify'
                                ) : (
                                    'Login'
                                )}
                            </Button>
                        </div>

                        <p className="mt-3 text-xs leading-5 text-zinc-400">
                            By logging in or continuing to use Project 314, you agree to the{' '}
                            <Link className="text-blue-400 hover:text-blue-300 underline" to="/beta-tester-agreement">
                                Beta Tester Agreement
                            </Link>
                            , the{' '}
                            <Link className="text-blue-400 hover:text-blue-300 underline" to="/terms-and-conditions">
                                Terms and Conditions
                            </Link>{' '}
                            and the{' '}
                            <Link className="text-blue-400 hover:text-blue-300 underline" to="/privacy-policy">
                                Privacy Policy
                            </Link>
                            .
                        </p>

                        <div className="mt-2">
                            <Link className="text-[#0275d8] hover:text-[#025aa5]" to="/register">
                                Don&apos;t have an account? Register
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default Login;
